import styled from 'styled-components';
import tw from 'twin.macro';
import { DateTime } from 'luxon';

// Activity type labels in Chinese/English
const ACTIVITY_TYPE_LABELS: Record<string, { label: string; icon: string; color: string }> = {
  booking_created: { label: 'Booking Created', icon: '📝', color: 'blue' },
  booking_confirmed: { label: 'Booking Confirmed', icon: '✅', color: 'green' },
  trip_started: { label: 'Trip Started', icon: '🚗', color: 'blue' },
  trip_completed: { label: 'Trip Completed', icon: '🏁', color: 'green' },
  trip_extended: { label: 'Trip Extended', icon: '📅', color: 'purple' },
  trip_shortened: { label: 'Trip Shortened', icon: '📅', color: 'orange' },
  car_swap_requested: { label: 'Car Swap Requested', icon: '🔄', color: 'yellow' },
  car_swap_approved: { label: 'Car Swap Approved', icon: '✅', color: 'green' },
  car_swap_rejected: { label: 'Car Swap Rejected', icon: '❌', color: 'red' },
  car_swap_completed: { label: 'Car Swap Completed', icon: '🔄', color: 'green' },
  car_swap_expired: { label: 'Car Swap Expired', icon: '⏰', color: 'gray' },
  cancelled_by_guest: { label: 'Cancelled by Guest', icon: '🚫', color: 'red' },
  cancelled_by_host: { label: 'Cancelled by Host', icon: '🚫', color: 'red' },
  cancelled_by_admin: { label: 'Cancelled by Admin', icon: '🚫', color: 'red' },
  cancellation_penalty_waived: { label: 'Penalty Waived', icon: '💰', color: 'green' },
  deposit_collected: { label: 'Deposit Collected', icon: '💳', color: 'blue' },
  deposit_authorized: { label: 'Deposit Authorized', icon: '💳', color: 'blue' },
  deposit_charged: { label: 'Deposit Charged', icon: '💳', color: 'yellow' },
  deposit_hold_requested: { label: 'Deposit Hold Requested', icon: '⏸️', color: 'orange' },
  deposit_released: { label: 'Deposit Released', icon: '💰', color: 'green' },
  deposit_partially_released: { label: 'Deposit Partially Released', icon: '💰', color: 'yellow' },
  deposit_captured: { label: 'Deposit Captured', icon: '💳', color: 'orange' },
  payment_received: { label: 'Payment Received', icon: '💵', color: 'green' },
  payment_failed: { label: 'Payment Failed', icon: '❌', color: 'red' },
  refund_initiated: { label: 'Refund Initiated', icon: '🔄', color: 'yellow' },
  refund_completed: { label: 'Refund Completed', icon: '💰', color: 'green' },
  refund_failed: { label: 'Refund Failed', icon: '❌', color: 'red' },
  driver_added: { label: 'Driver Added', icon: '👤', color: 'blue' },
  driver_removed: { label: 'Driver Removed', icon: '👤', color: 'gray' },
  protection_plan_changed: { label: 'Protection Plan Changed', icon: '🛡️', color: 'purple' },
  coupon_applied: { label: 'Coupon Applied', icon: '🎫', color: 'green' },
  trip_start_photo_uploaded: { label: 'Start Photos Uploaded', icon: '📷', color: 'blue' },
  trip_end_photo_uploaded: { label: 'End Photos Uploaded', icon: '📷', color: 'blue' },
  dl_verification_submitted: { label: 'DL Verification Submitted', icon: '🪪', color: 'blue' },
  dl_verification_verified: { label: 'DL Verification Verified', icon: '✅', color: 'green' },
  dl_verification_rejected: {
    label: 'Cancelled - DL Verification Not Passed',
    icon: '🚫',
    color: 'red',
  },
  host_checkin_completed: { label: 'Host Check-in Completed', icon: '✅', color: 'green' },
  guest_checkin_completed: { label: 'Guest Check-in Completed', icon: '✅', color: 'green' },
};

const TimelineContainer = styled.div`
  ${tw`space-y-4`}
`;

const TimelineHeader = styled.div`
  ${tw`flex justify-between items-center mb-4`}
`;

const TimelineTitle = styled.h3`
  ${tw`text-lg font-semibold text-text-primary`}
`;

const TimelineCount = styled.span`
  ${tw`text-sm text-text-secondary`}
`;

const TimelineList = styled.div`
  ${tw`relative`}
`;

const TimelineLine = styled.div`
  ${tw`absolute left-4 top-0 bottom-0 w-0.5 bg-bg-secondary`}
`;

const TimelineItem = styled.div`
  ${tw`relative flex items-start gap-4 pb-6 last:pb-0`}
`;

const TimelineDot = styled.div<{ color: string }>`
  ${tw`relative z-10 flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm`}
  ${({ color }) => {
    switch (color) {
      case 'green':
        return tw`bg-green-100 text-green-600`;
      case 'red':
        return tw`bg-red-100 text-red-600`;
      case 'yellow':
        return tw`bg-yellow-100 text-yellow-600`;
      case 'orange':
        return tw`bg-orange-100 text-orange-600`;
      case 'purple':
        return tw`bg-purple-100 text-black`;
      case 'gray':
        return tw`bg-bg-secondary text-text-secondary`;
      default:
        return tw`bg-bg-secondary text-text-secondary`;
    }
  }}
`;

const TimelineContent = styled.div`
  ${tw`flex-1 min-w-0`}
`;

const TimelineItemHeader = styled.div`
  ${tw`flex flex-wrap items-center gap-2 mb-1`}
`;

const ActivityLabel = styled.span`
  ${tw`font-medium text-text-primary`}
`;

const ActivityAmount = styled.span<{ isNegative?: boolean }>`
  ${tw`text-sm font-medium px-2 py-0.5 rounded-md`}
  ${({ isNegative }) =>
    isNegative ? tw`bg-red-100 text-red-700` : tw`bg-green-100 text-green-700`}
`;

const TimelineItemMeta = styled.div`
  ${tw`flex flex-wrap items-center gap-2 text-sm text-text-secondary`}
`;

const TimelineItemNote = styled.div`
  ${tw`mt-1 text-sm text-text-secondary bg-bg-secondary rounded-md px-2 py-1`}
`;

const StateChange = styled.div`
  ${tw`mt-1 text-xs text-text-secondary flex flex-wrap gap-2`}
`;

const StateItem = styled.span`
  ${tw`bg-bg-secondary px-2 py-0.5 rounded-md`}
`;

const EmptyState = styled.div`
  ${tw`text-center py-8 text-text-secondary`}
`;

interface BookingActivity {
  id: string;
  activityType: string;
  previousState?: Record<string, unknown> | null;
  newState?: Record<string, unknown> | null;
  amount?: number | null;
  invoiceId?: string | null;
  initiatedBy: string;
  note?: string | null;
  metadata?: Record<string, unknown> | null;
  createdAt: string;
  initiatedByUser?: {
    id: string;
    firstName?: string | null;
    lastName?: string | null;
    displayName?: string | null;
    email?: string | null;
  } | null;
}

interface BookingActivityTimelineProps {
  activities: BookingActivity[];
  timezone?: string;
}

const formatTimestamp = (timestamp: string, timezone?: string): string => {
  const dt = DateTime.fromISO(timestamp);
  const tz = timezone || 'America/Los_Angeles';
  return dt.setZone(tz).toFormat('MMM dd, yyyy h:mm a');
};

const formatAmount = (amount: number): string => {
  const formatted = Math.abs(amount).toFixed(2);
  return amount < 0 ? `-$${formatted}` : `$${formatted}`;
};

const getInitiatorName = (activity: BookingActivity): string => {
  if (activity.initiatedByUser) {
    const user = activity.initiatedByUser;
    if (user.displayName) return user.displayName;
    if (user.firstName || user.lastName) {
      return `${user.firstName || ''} ${user.lastName || ''}`.trim();
    }
    return user.email || 'Unknown User';
  }
  return 'System';
};

const formatStateChange = (
  previousState: Record<string, unknown> | null | undefined,
  newState: Record<string, unknown> | null | undefined,
): { key: string; from: string; to: string }[] => {
  const changes: { key: string; from: string; to: string }[] = [];

  if (!previousState && !newState) return changes;

  const allKeys = new Set([...Object.keys(previousState || {}), ...Object.keys(newState || {})]);

  allKeys.forEach(key => {
    const from = previousState?.[key];
    const to = newState?.[key];
    if (from !== to) {
      let fromStr = from !== undefined ? String(from) : '-';
      let toStr = to !== undefined ? String(to) : '-';

      // Format timestamps
      if (key === 'endAt' || key === 'startAt') {
        if (typeof from === 'number') {
          fromStr = DateTime.fromMillis(from).toFormat('MMM dd, yyyy');
        }
        if (typeof to === 'number') {
          toStr = DateTime.fromMillis(to).toFormat('MMM dd, yyyy');
        }
      }

      changes.push({ key, from: fromStr, to: toStr });
    }
  });

  return changes;
};

export const BookingActivityTimeline: React.FC<BookingActivityTimelineProps> = ({
  activities,
  timezone,
}) => {
  if (!activities || activities.length === 0) {
    return (
      <TimelineContainer>
        <TimelineHeader>
          <TimelineTitle>Activity Timeline</TimelineTitle>
        </TimelineHeader>
        <EmptyState>No activity recorded yet</EmptyState>
      </TimelineContainer>
    );
  }

  // Sort activities by createdAt descending (most recent first)
  const sortedActivities = [...activities].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );

  return (
    <TimelineContainer>
      <TimelineHeader>
        <TimelineTitle>Activity Timeline</TimelineTitle>
        <TimelineCount>{activities.length} events</TimelineCount>
      </TimelineHeader>
      <TimelineList>
        <TimelineLine />
        {sortedActivities.map(activity => {
          const activityKey = activity.activityType.toLowerCase();
          const activityInfo = ACTIVITY_TYPE_LABELS[activityKey] || {
            label: activity.activityType.replace(/_/g, ' '),
            icon: '📌',
            color: 'gray',
          };
          const stateChanges = formatStateChange(activity.previousState, activity.newState);

          return (
            <TimelineItem key={activity.id}>
              <TimelineDot color={activityInfo.color}>{activityInfo.icon}</TimelineDot>
              <TimelineContent>
                <TimelineItemHeader>
                  <ActivityLabel>{activityInfo.label}</ActivityLabel>
                  {activity.amount !== null &&
                    activity.amount !== undefined &&
                    activity.amount !== 0 && (
                      <ActivityAmount isNegative={activity.amount < 0}>
                        {formatAmount(activity.amount)}
                      </ActivityAmount>
                    )}
                </TimelineItemHeader>
                <TimelineItemMeta>
                  <span>{formatTimestamp(activity.createdAt, timezone)}</span>
                  <span>•</span>
                  <span>{getInitiatorName(activity)}</span>
                </TimelineItemMeta>
                {activity.note && <TimelineItemNote>{activity.note}</TimelineItemNote>}
                {stateChanges.length > 0 && (
                  <StateChange>
                    {stateChanges.map(change => (
                      <StateItem key={change.key}>
                        {change.key}: {change.from} → {change.to}
                      </StateItem>
                    ))}
                  </StateChange>
                )}
              </TimelineContent>
            </TimelineItem>
          );
        })}
      </TimelineList>
    </TimelineContainer>
  );
};

export default BookingActivityTimeline;
